import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { createClosingStock, alterClosingStock, deleteClosingStock, listClosingStock, stockValueToday, type ClosingStock } from '@/api/accountsReports';
import { amt, tdate, today } from './reportUtils';

const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';
type Draft = { id: number | null; asOn: string; amount: string; note: string };

/**
 * Closing Stock: the stock's value as on a date, entered by Accounts. Profit & Loss and the
 * Balance Sheet use the latest entry on or before their date.
 */
export function ClosingStockPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [draft, setDraft] = useState<Draft | null>(null);
  const q = useQuery({ queryKey: ['acc-books', 'closing-stock'], queryFn: listClosingStock });
  const rows = q.data ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['acc-books'] });

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const body = { asOn: d.asOn, amount: Number(d.amount), note: d.note.trim() || null };
      return d.id ? alterClosingStock(d.id, body) : createClosingStock(body);
    },
    onSuccess: () => { toast.push('Closing stock saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const delMut = useMutation({
    mutationFn: (id: number) => deleteClosingStock(id),
    onSuccess: () => { toast.push('Closing stock deleted', 'success'); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });
  const todayMut = useMutation({
    mutationFn: stockValueToday,
    onSuccess: (v) => draft && setDraft({ ...draft, asOn: v.asOn, amount: String(v.total), note: `Warehouse ₹${amt(v.warehouse) || '0.00'} + SIS stores ₹${amt(v.sis) || '0.00'} at purchase rate` }),
    onError: (e) => toast.push(apiError(e, 'Could not value today’s stock'), 'error'),
  });

  return (
    <div>
      <SectionHeader
        title="Closing Stock"
        actions={<Button variant="primary" size="sm" onClick={() => setDraft({ id: null, asOn: today(), amount: '', note: '' })}><Plus size={14} /> Enter closing stock</Button>}
      />
      <p className="mb-2 text-xs text-[var(--text-secondary)]">
        Profit &amp; Loss and the Balance Sheet use the latest value on or before their date. Without one, stock is taken as unchanged.
      </p>
      <Table<ClosingStock>
        storageKey="acc-books-closing-stock"
        title="Closing Stock"
        count={`${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={q.isFetching && rows.length === 0}
        empty="No closing stock entered yet."
        columns={[
          { key: 'asOn', header: 'As on', width: '110px', cell: (r) => <strong>{tdate(r.asOn)}</strong> },
          { key: 'amount', header: 'Stock value', width: '150px', align: 'right', cell: (r) => <span className="font-mono">₹{amt(r.amount) || '0.00'}</span> },
          { key: 'note', header: 'Note', minWidth: '180px', cell: (r) => <span className="text-[var(--text-muted)]">{r.note || '—'}</span> },
          { key: 'by', header: 'Entered by', width: '140px', cell: (r) => r.createdBy ?? '—' },
          {
            key: 'actions', header: '', width: '110px', align: 'right', alwaysVisible: true,
            cell: (r) => (
              <div className="flex items-center justify-end gap-1">
                <Button variant="glass" size="sm" onClick={() => setDraft({ id: r.id, asOn: r.asOn, amount: String(r.amount), note: r.note ?? '' })}>Alter</Button>
                <button type="button" onClick={async () => { if (await confirmDlg(`Delete closing stock as on ${tdate(r.asOn)}?`)) delMut.mutate(r.id); }} className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete"><Trash2 size={14} /></button>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Alter Closing Stock' : 'Enter Closing Stock'}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" disabled={saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>{saveMut.isPending ? 'Saving…' : 'Save'}</Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={label}>As on</label>
                <Input type="date" max={today()} value={draft.asOn} onChange={(e) => setDraft({ ...draft, asOn: e.target.value })} />
              </div>
              <div>
                <label className={label}>Stock value (₹)</label>
                <Input type="number" min={0} step="0.01" className="text-right font-mono" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={label}>Note</label>
              <Input value={draft.note} maxLength={200} placeholder="e.g. Physical count, valued at cost" onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
            </div>
            <Button variant="glass" size="sm" disabled={todayMut.isPending} onClick={() => todayMut.mutate()}>
              {todayMut.isPending ? 'Valuing…' : 'Use today’s stock value'}
            </Button>
            <p className="text-[11px] text-[var(--text-muted)]">Today&rsquo;s value: warehouse and SIS store quantities × each item&rsquo;s purchase rate. Check it before saving.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
